import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';

interface ContactDocumentsBlock {
  key: 'invoices' | 'expenses';
  title: string;
  icon: string;
  url: string;
  paidLabel: string;
  totalLabel: string;
  emptyText: string;
  loading: boolean;
  error: boolean;
  count: number;
  total: number;
  paid: number;
  pending: number;
  percent: number;
  items: any[];
}

@Component({
  selector: 'app-contact-detail-tabs',
  templateUrl: './contact-detail-tabs.component.html',
  styleUrls: ['./contact-detail-tabs.component.scss'],
  standalone: false
})
export class ContactDetailTabsComponent implements OnInit, OnDestroy {
  @Input() row: any;

  readonly recentCount = 5;
  blocks: ContactDocumentsBlock[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(private http: HttpClient) { }

  ngOnInit(): void {
    if (!this.row) return;
    if (this.row.isClient) {
      this.blocks.push(this.createBlock('invoices', 'Facturas emitidas', 'receipt', '/api/v2/invoices/list', 'Cobrado', 'Facturado', 'Este cliente todavía no tiene facturas en la actividad seleccionada.'));
    }
    if (this.row.isProvider) {
      this.blocks.push(this.createBlock('expenses', 'Gastos recibidos', 'payment', '/api/v2/expenses/list', 'Pagado', 'Gastado', 'Este proveedor todavía no tiene gastos en la actividad seleccionada.'));
    }
    this.blocks.forEach(block => this.load(block));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get initials(): string {
    const name = String((this.row && this.row.companyName) || '').trim();
    if (!name) return '?';
    const words = name.split(/\s+/).filter(Boolean);
    return ((words[0] || '').charAt(0) + (words.length > 1 ? words[1].charAt(0) : '')).toUpperCase();
  }

  get typeLabel(): string {
    if (!this.row) return '';
    if (this.row.isClient && this.row.isProvider) return 'Cliente y proveedor';
    if (this.row.isClient) return 'Cliente';
    if (this.row.isProvider) return 'Proveedor/Acreedor';
    return 'Sin tipo';
  }

  get address(): string {
    if (!this.row) return '';
    const place = [this.row.postalCode, this.row.location].filter(Boolean).join(' ');
    return [this.row.address, place, this.row.state].filter(Boolean).join(', ');
  }

  get accountLines(): { label: string; value: string }[] {
    if (!this.row) return [];
    const lines: { label: string; value: string }[] = [];
    if (this.row.isClient) {
      lines.push({ label: 'Cuenta de cliente', value: this.row.accountNumberClient });
      lines.push({ label: 'Cuenta de ingreso', value: this.row.entranceAccountClient });
    }
    if (this.row.isProvider) {
      lines.push({ label: 'Cuenta de proveedor', value: this.row.accountNumberProvider });
      lines.push({ label: 'Cuenta de gasto', value: this.row.expenseAccountProvider || this.row.entranceAccountProvider });
    }
    return lines;
  }

  reload(block: ContactDocumentsBlock): void {
    this.load(block);
  }

  pendingOf(doc: any): number {
    return Math.max(0, (Number(doc.total) || 0) - (Number(doc.totalPaided) || 0));
  }

  isPaid(doc: any): boolean {
    return !!doc.isPaided || this.pendingOf(doc) < 0.005;
  }

  isPartial(doc: any): boolean {
    return !this.isPaid(doc) && (Number(doc.totalPaided) || 0) > 0;
  }

  trackByUid(_: number, item: any): string {
    return item.uid;
  }

  private load(block: ContactDocumentsBlock): void {
    if (block.loading) return;
    block.loading = true;
    block.error = false;
    this.http.post<any>(`${environment.apiUrl}${block.url}`, {
      page: 1,
      pageSize: this.recentCount,
      sortField: 'InvoiceRelease',
      sortDirection: 'desc',
      receiverUid: this.row.uid,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: res => {
        block.loading = false;
        if (!res || res.isSuccess === false) {
          block.error = true;
          return;
        }
        const summary = res.summary || {};
        block.items = res.values || [];
        block.count = res.total || 0;
        block.total = Number(summary.total) || 0;
        block.paid = Number(summary.totalPaided) || 0;
        block.pending = Number(summary.totalNotPaided) || Math.max(0, block.total - block.paid);
        block.percent = block.total > 0 ? Math.min(100, Math.round((block.paid / block.total) * 100)) : 0;
      },
      error: () => {
        block.loading = false;
        block.error = true;
      },
    });
  }

  private createBlock(key: 'invoices' | 'expenses', title: string, icon: string, url: string, paidLabel: string, totalLabel: string, emptyText: string): ContactDocumentsBlock {
    return {
      key, title, icon, url, paidLabel, totalLabel, emptyText,
      loading: false, error: false, count: 0, total: 0, paid: 0, pending: 0, percent: 0, items: [],
    };
  }
}
